import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { Link } from 'react-router-dom';
import { Layout } from 'antd';
import HeaderBar from 'Layout/HeaderBar';
import FooterBar from 'Layout/FooterBar';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import catalogue from 'data/machineCatalogue.json';
import useMachineCatalogue, { CatalogueMachine, manualCapacityInput, saveManualCapacity } from 'hooks/useMachineCatalogue';
import InventoryNav from './InventoryNav';

const brands = Array.from(new Set(catalogue.map((machine) => machine.brand))).sort();
const numberFormat = new Intl.NumberFormat('da-DK');

export default function MachineCatalogue() {
  const { machines, ready, error } = useMachineCatalogue();
  const [editing, setEditing] = useState<CatalogueMachine | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useMemo(() => debounce(setSearch, 300), []);
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  const [brand, setBrand] = useState('');
  const [knownTankOnly, setKnownTankOnly] = useState(false);
  const query = search.trim().toLocaleLowerCase('da-DK');
  const filtered = machines.filter(
    (machine) =>
      (!brand || machine.brand === brand) &&
      (!knownTankOnly || machine.solutionTankLiters != null) &&
      `${machine.brand} ${machine.model} ${machine.machineType} ${machine.variants}`
        .toLocaleLowerCase('da-DK')
        .includes(query)
  );

  return (
    <>
      <Layout className="min-w-0">
        <HeaderBar />
        <Layout.Content className="p-4 md:p-6">
          <div className="mb-6">
            <InventoryNav />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>
                <h1 className="text-2xl">Maskinkatalog</h1>
              </CardTitle>
              <CardDescription>
                {machines.length} modelfamilier fra AKS2TALs katalog. Modeloplysninger, ikke en
                liste over ejede maskiner.
              </CardDescription>
              <CardDescription>
                Ukendt betyder ikke verificeret. Bekræft variant, faktisk fyldemængde og monteret
                doseringssystem på maskinen. Kildetekster er bevaret på originalsproget.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error ? <p role="alert">{error}</p> : !ready && (
                <p role="status">Henter gemte kapaciteter. Manuel registrering kræver forbindelse.</p>
              )}
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-48 flex-1">
                  <Label htmlFor="machine-search" className="mb-1 block text-sm font-medium">
                    Søg model eller variant
                  </Label>
                  <Input
                    id="machine-search"
                    type="search"
                    onChange={(event) => debouncedSearch(event.target.value)}
                    placeholder="Fx Nilfisk SC500"
                  />
                </div>
                <div>
                  <Label htmlFor="machine-brand" className="mb-1 block text-sm font-medium">
                    Mærke
                  </Label>
                  <Select
                    value={brand || 'all'}
                    onValueChange={(value) => setBrand(value === 'all' ? '' : value)}
                  >
                    <SelectTrigger id="machine-brand" className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle mærker</SelectItem>
                      {brands.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex min-h-9 items-center gap-2">
                  <Checkbox
                    id="known-tank"
                    checked={knownTankOnly}
                    onCheckedChange={(checked) => setKnownTankOnly(checked === true)}
                  />
                  <Label htmlFor="known-tank">Kun med kendt rentvandstank</Label>
                </div>
              </div>
              <p role="status" className="text-sm text-muted-foreground">
                {filtered.length} af {machines.length} modeller
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Mærke / model</TableHead>
                    <TableHead scope="col">Type</TableHead>
                    <TableHead scope="col">Rentvandstank</TableHead>
                    <TableHead scope="col">Automatisk dosering</TableHead>
                    <TableHead scope="col">Specifikationer</TableHead>
                    <TableHead scope="col">Beregning</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        Ingen modeller matcher. Prøv et andet navn eller fjern et filter.
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((machine) => (
                    <TableRow key={machine.id}>
                      <TableCell className="align-top">
                        <div className="flex items-center gap-3">
                          <div className="w-12 shrink-0">
                            <MachinePhoto machine={machine} />
                          </div>
                          <div className="min-w-0 leading-snug">
                            <strong>{machine.brand}</strong>
                            <div>{machine.model}</div>
                            {machine.image?.match === 'family' && (
                              <p className="mb-0 mt-1 text-xs text-muted-foreground">
                                Seriefoto · variant kan afvige
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">{machine.machineType}</TableCell>
                      <TableCell className="whitespace-nowrap align-top">
                        {machine.solutionTankLiters == null
                          ? 'Ukendt'
                          : `${numberFormat.format(machine.solutionTankLiters)} L`}
                        {machine.manualCapacity && (
                          <p className="mt-1 text-xs text-muted-foreground">Manuelt indtastet</p>
                        )}
                        {(machine.solutionTankLiters == null || machine.manualCapacity) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 block"
                            disabled={!ready}
                            onClick={() => setEditing(machine)}
                          >
                            {machine.manualCapacity ? 'Ret kapacitet' : 'Tilføj kapacitet'}
                            <span className="sr-only">: {machine.brand} {machine.model}</span>
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="align-top">{machine.automaticDosing}</TableCell>
                      <TableCell className="min-w-56 max-w-xl align-top">
                        <Accordion type="single" collapsible>
                          <AccordionItem value="specifications" className="border-0">
                            <AccordionTrigger className="py-0">
                              Vis data og kilder
                              <span className="sr-only">
                                {' '}
                                for {machine.brand} {machine.model}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <dl className="mt-3 space-y-3 break-words">
                                {machine.manualCapacity && (
                                  <div>
                                    <dt className="font-medium">Manuel kapacitet · kilde og variant</dt>
                                    <dd className="m-0 whitespace-pre-wrap">{machine.manualCapacity.note}</dd>
                                  </div>
                                )}
                                {[
                                  ['Variant', machine.variants],
                                  [
                                    'Opsamlingstank',
                                    machine.recoveryTankLiters == null
                                      ? 'Ukendt'
                                      : `${numberFormat.format(machine.recoveryTankLiters)} L`,
                                  ],
                                  ['Autonom rengøring', machine.autonomy],
                                  ['Dosering / koncentration', machine.dosingSystem],
                                  [
                                    'Vandtilførsel',
                                    [machine.waterDelivery, machine.flowUnit]
                                      .filter(Boolean)
                                      .join(' '),
                                  ],
                                  ['Vandstyring / tilstand', machine.waterControl],
                                  ['Påfyldning, tømning og opladning', machine.servicing],
                                  ['AKS opsætning', machine.setupChecks],
                                  ['Tankkilde', machine.tankEvidence],
                                  ['Doseringskilde', machine.dosingEvidence],
                                  ['Flowkilde', machine.flowEvidence],
                                  [
                                    'Modelreference',
                                    `${machine.modelReference} (${machine.modelEvidenceType}, ${machine.sourceMarket})`,
                                  ],
                                  ['Kontrolleret i kataloget', machine.checked],
                                  ['Fotoreference', machine.image?.reference || machine.photoNotes],
                                  ['Fotokilde', machine.image?.sourceUrl || machine.photoSource],
                                ].map(([label, value]) => (
                                  <div key={label}>
                                    <dt className="font-medium">{label}</dt>
                                    <dd className="m-0 whitespace-pre-wrap">
                                      {value || 'Ikke verificeret'}
                                    </dd>
                                  </div>
                                ))}
                              </dl>
                              {machine.operatingManuals?.map((manual) => (
                                <div key={manual.url} className="mt-3">
                                  <Button asChild variant="link" className="h-auto p-0">
                                    <a href={manual.url} target="_blank" rel="noreferrer">
                                      Åbn betjeningsvejledning
                                      <span className="sr-only">: {manual.reference}</span>
                                    </a>
                                  </Button>
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {manual.reference}
                                  </p>
                                </div>
                              ))}
                              <Button asChild variant="link" className="mt-3 h-auto p-0">
                                <a href={machine.modelSourceUrl} target="_blank" rel="noreferrer">
                                  Åbn modelkilde
                                </a>
                              </Button>
                              {machine.marketEvidence && (
                                <Button asChild variant="link" className="ml-4 mt-3 h-auto p-0">
                                  <a href={machine.marketEvidence} target="_blank" rel="noreferrer">
                                    Åbn dansk markedsreference
                                  </a>
                                </Button>
                              )}
                              <p className="mt-2 text-xs text-muted-foreground">
                                {machine.sourceRow == null
                                  ? `Webkilder · kontrolleret ${machine.checked}`
                                  : `aks2tal-machine-catalogue.xlsx · Machines · række ${machine.sourceRow}`}
                              </p>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </TableCell>
                      <TableCell className="align-top">
                        <Button asChild variant="outline" size="sm">
                          <Link
                            to={`/dust-binder-calculator?machine=${encodeURIComponent(machine.id)}`}
                          >
                            Brug i beregner
                            <span className="sr-only">
                              : {machine.brand} {machine.model}
                            </span>
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Layout.Content>
        <FooterBar />
      </Layout>
      {editing && <CapacityDialog machine={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function MachinePhoto({ machine }: { machine: CatalogueMachine }) {
  const [failed, setFailed] = useState(false);
  const { image } = machine;
  if (!image) return <p className="m-0 text-xs text-muted-foreground">Foto mangler</p>;
  const name = `${machine.brand} ${machine.model}`;
  return (
    <Dialog>
      {failed ? (
        <p className="m-0 text-xs text-muted-foreground">Billedet kunne ikke hentes</p>
      ) : (
        <DialogTrigger asChild>
          <Button variant="outline" className="h-12 w-12 bg-white p-1" aria-label={`Vis billede af ${name}`}>
            <img src={image.url} alt={name} width={40} height={40} loading="lazy" decoding="async"
              className="h-full w-full object-contain" onError={() => setFailed(true)} />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
          <DialogDescription>
            {image.match === 'family' ? 'Foto af modelserien. Variant og udstyr kan afvige.' : 'Referencefoto af modellen. Monteret udstyr kan afvige.'}
          </DialogDescription>
        </DialogHeader>
        {failed ? <p role="status">Billedet kunne ikke hentes. Prøv billedkilden nedenfor.</p> : (
          <img src={image.url} alt={name} className="max-h-[55vh] w-full rounded-md bg-white object-contain"
            onError={() => setFailed(true)} />
        )}
        <p className="text-sm text-muted-foreground">{image.reference}</p>
        <Button asChild variant="link" className="h-auto justify-start p-0">
          <a href={image.sourceUrl} target="_blank" rel="noreferrer">Åbn billedkilde</a>
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CapacityDialog({ machine, onClose }: { machine: CatalogueMachine; onClose: () => void }) {
  const [capacity, setCapacity] = useState(String(machine.manualCapacity?.solutionTankLiters ?? ''));
  const [note, setNote] = useState(machine.manualCapacity?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rentvandstank · {machine.brand} {machine.model}</DialogTitle>
          <DialogDescription>
            Åbn kilden og kontrollér rentvandstankens fyldemængde og den præcise variant.
            Værdien gemmes for modellen og deles med AKS i kataloget og beregneren.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {machine.operatingManuals?.map((manual) => (
            <div key={manual.url}>
              <Button asChild variant="link" className="h-auto whitespace-normal p-0">
                <a href={manual.url} target="_blank" rel="noreferrer">Åbn betjeningsvejledning</a>
              </Button>
              <p className="text-xs text-muted-foreground">{manual.reference}</p>
            </div>
          ))}
          {!machine.operatingManuals?.length && (
            <p className="text-sm text-muted-foreground">Ingen separat betjeningsvejledning er linket til denne model endnu.</p>
          )}
          <Button asChild variant="link" className="h-auto p-0">
            <a href={machine.modelSourceUrl} target="_blank" rel="noreferrer">Åbn modelkilde</a>
          </Button>
          <p className="text-sm">{machine.variants}</p>
        </div>
        <form className="space-y-4" noValidate onSubmit={async (event) => {
          event.preventDefault();
          if (saving) return;
          const parsed = manualCapacityInput.safeParse({
            solutionTankLiters: Number(capacity.trim().replace(',', '.')), note,
          });
          if (!parsed.success) {
            setError('Angiv en kapacitet over 0 og højst 10.000 liter samt en kilde/variant (højst 1.000 tegn).');
            return;
          }
          setSaving(true);
          setError('');
          try {
            await saveManualCapacity(machine.id, parsed.data, machine.manualCapacity?.version ?? 0);
            onClose();
          } catch (failure) {
            setError(failure instanceof Error && !('code' in failure)
              ? failure.message
              : 'Kunne ikke gemme. Kontrollér forbindelse og adgang. Dine indtastninger er bevaret.');
          } finally {
            setSaving(false);
          }
        }}>
          <div className="space-y-2">
            <Label htmlFor="manual-tank-capacity">Rentvandstank (liter)</Label>
            <Input id="manual-tank-capacity" inputMode="decimal" value={capacity}
              disabled={saving} onChange={(event) => setCapacity(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="manual-tank-note">Kilde og variant</Label>
            <Textarea id="manual-tank-note" value={note} maxLength={1000} disabled={saving}
              placeholder="Fx manual side 23, type og årgang fra typeskiltet"
              onChange={(event) => setNote(event.target.value)} />
          </div>
          {error && <p role="alert">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={onClose}>Annuller</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Gemmer…' : 'Gem kapacitet'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
